import type {
  SdkApprovalResponse,
  SdkApprovalResult,
} from "@magnus/contract/approvals";
import type { SdkArtifactPickerItem } from "@magnus/contract/artifacts";
import type {
  SdkFavoriteRoomsInput,
  SdkFavoriteRoomsResponse,
  SdkRoomLookupQuery,
  SdkRoomsResponse,
} from "@magnus/contract/calendar";
import type {
  SdkChatListPage,
  SdkChatMessages,
  SdkChatRenameInput,
  SdkChatSummary,
} from "@magnus/contract/chats";
import type {
  SdkFeedbackMap,
  SdkFeedbackSubmitInput,
} from "@magnus/contract/feedback";
import type { SdkModelList } from "@magnus/contract/models";
import type { SdkSkillPickerItem } from "@magnus/contract/skills";
import type {
  SdkUploadCreateInput,
  SdkUploadFinalized,
  SdkUploadMinted,
  SdkUploadStatus,
} from "@magnus/contract/uploads";
import {
  LIVE_SESSION_RELEASE_PARAM,
  type SdkLiveSessionAnswer,
  type SdkLiveSessionRequest,
  type SdkLiveVoiceAvailability,
  type SdkVoiceSessionSaved,
  type SdkVoiceSessionSaveInput,
  type SdkVoiceToken,
  type SdkVoiceTokenGrant,
  type SdkVoiceTtsRequest,
} from "@magnus/contract/voice";

/**
 * Typed methods over the versioned REST surface (`/api/sdk/v1`, plan 015).
 * Plain JSON — no superjson, no tRPC semantics — callable from any stack
 * that has fetch. Errors stay dependency-free: a non-2xx response throws
 * an `Error` carrying the status and the response body text (no error
 * classes in v1).
 *
 * The casts at each call site are the authored-contract trust model (same
 * as `chats.create`): the server guarantees the wire shape and the SDK
 * promises the authored `@magnus/contract` type. What keeps the two honest
 * is server-side: each route handler annotates its payload with the contract
 * type it serializes, and the request bodies are pinned key-for-key by
 * `apps/web/app/api/sdk/v1/__tests__/contract-drift-check.ts`.
 */

/**
 * A response the caller branches on rather than throws for: the live-call
 * mint (403 / 503 / 409 / 429 each mean something different to the UI) and
 * the recap save (retried on transient statuses). A network failure still
 * rejects.
 */
export type SdkRestResult<T> =
  | { ok: true; value: T }
  | {
      ok: false;
      status: number;
      /** The `{ error, code }` envelope's `code`, when the body had one. */
      code?: string;
      /** From `Retry-After`, in seconds. */
      retryAfterSeconds?: number;
    };

export interface SdkRestSurface {
  readonly artifacts: {
    /** Picker items for the `@`-mention artifact picker ([] when gated off). */
    listPickerItems(): Promise<SdkArtifactPickerItem[]>;
  };
  /**
   * Meeting-booking lookups behind the in-chat booking dialog. These sit on
   * the unversioned host routes (`/api/calendar/*`) rather than `/api/sdk/v1`
   * — they exist for the SDK's own booking UI, not as a public surface — but
   * they go through the same auth-applied fetch so an embedded host with
   * bearer auth and a remote `baseUrl` reaches them like any other call.
   */
  readonly calendar: {
    /** Starred rooms, newest star last. `[]` when none. */
    getFavoriteRooms(options?: {
      signal?: AbortSignal;
    }): Promise<SdkFavoriteRoomsResponse>;
    /** Free rooms for one meeting window, server-ranked. */
    listRooms(
      query: SdkRoomLookupQuery,
      options?: { signal?: AbortSignal }
    ): Promise<SdkRoomsResponse>;
    /** Replace the starred list (send the full list; last write wins). */
    setFavoriteRooms(
      input: SdkFavoriteRoomsInput,
      options?: { signal?: AbortSignal }
    ): Promise<SdkFavoriteRoomsResponse>;
  };
  readonly chats: {
    /** List the caller's chats, most-recent first. Cursor-paginated. */
    list(input?: { limit?: number; cursor?: string }): Promise<SdkChatListPage>;
    /** One chat plus its persisted messages (history hydration). */
    get(chatId: string): Promise<SdkChatMessages>;
    /** Rename a chat; resolves the updated summary (ISO dates). */
    rename(chatId: string, input: SdkChatRenameInput): Promise<SdkChatSummary>;
    /** Hard-delete a chat (204, no body). */
    delete(chatId: string): Promise<void>;
    /**
     * Save a finished live call's recap (plan 042). Idempotent on
     * `sessionId`. `keepalive` lets the save outlive an unloading page
     * (browsers cap that body at 64KB).
     */
    saveVoiceSession(
      chatId: string,
      input: SdkVoiceSessionSaveInput,
      options?: { keepalive?: boolean }
    ): Promise<SdkRestResult<SdkVoiceSessionSaved>>;
  };
  readonly feedback: {
    /** The caller's thumbs state for a chat, keyed by message id. */
    get(chatId: string): Promise<SdkFeedbackMap>;
    /** Submit a thumbs vote on a message. */
    submit(chatId: string, input: SdkFeedbackSubmitInput): Promise<void>;
  };
  readonly models: {
    /** The caller's selectable models (server-side flag-filtered) + default. */
    list(): Promise<SdkModelList>;
  };
  readonly skills: {
    /** Picker items for the skill slash-picker, each with its category key. */
    listPickerItems(): Promise<SdkSkillPickerItem[]>;
  };
  readonly toolApprovals: {
    /** Resolve a HITL approval pause (`data-approval-request`) with the
     *  user's decision; the paused turn resumes server-side. */
    submit(input: SdkApprovalResponse): Promise<SdkApprovalResult>;
  };
  readonly uploads: {
    /** Phase 1: mint a write-only SAS for a direct-to-blob PUT. */
    create(input: SdkUploadCreateInput): Promise<SdkUploadMinted>;
    /** Phase 3: verify the landed bytes and kick off processing. Resolves
     *  the finalized row (url/storageKey/processingStatus); ignore it unless
     *  you need those fields. `signal` aborts the request (a remove racing
     *  finalize). */
    finalize(
      uploadId: string,
      options?: { signal?: AbortSignal }
    ): Promise<SdkUploadFinalized>;
    /** One upload's status. */
    get(uploadId: string): Promise<SdkUploadStatus>;
    /** Delete the row and its blobs. */
    delete(uploadId: string): Promise<void>;
    /** Re-run processing for a failed upload (or a failed rich pass).
     *  Rejects with a 400 when the upload isn't in a retryable state; poll
     *  `get` for the new pipeline status. */
    retryProcessing(uploadId: string): Promise<void>;
  };
  readonly voice: {
    /**
     * Mint a short-lived, least-privilege Cartesia access token
     * (`/api/sdk/v1/voice/ws-token`) so the client can call Cartesia
     * directly (WS or batch HTTP) without the server-held API key.
     * Cache the result across turns — minting is a rate-limited write.
     */
    mintToken(grant: SdkVoiceTokenGrant): Promise<SdkVoiceToken>;
    /**
     * HTTP TTS fallback (`/api/sdk/v1/voice/tts`): synthesizes one chunk
     * of text and resolves the MP3 bytes. Prefer the direct-WS path
     * (with a `tts` token) for live playback.
     */
    tts(input: SdkVoiceTtsRequest): Promise<ArrayBuffer>;
    /** Whether the caller can start a live call (flag on, server keyed). */
    liveAvailability(): Promise<SdkLiveVoiceAvailability>;
    /**
     * Exchange a WebRTC offer for a GPT-Live session (plan 042). The
     * server holds the OpenAI key and builds the session config.
     */
    liveSession(
      input: SdkLiveSessionRequest,
      options?: { signal?: AbortSignal }
    ): Promise<SdkRestResult<SdkLiveSessionAnswer>>;
    /** Hang up: frees the one-call-per-user lease `callSessionId` holds. */
    endLiveSession(
      callSessionId: string,
      options?: { keepalive?: boolean }
    ): Promise<void>;
  };
}

async function requestResult<T>(
  fetch: typeof globalThis.fetch,
  method: string,
  url: string,
  body: unknown,
  init?: { signal?: AbortSignal; keepalive?: boolean }
): Promise<SdkRestResult<T>> {
  const res = await fetch(url, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    signal: init?.signal,
    keepalive: init?.keepalive,
  });
  return readSdkRestResult<T>(res);
}

/** Reads a response as an `SdkRestResult`: the JSON body on 2xx, else the
 *  status with the envelope's `code` and `Retry-After`. */
export async function readSdkRestResult<T>(
  res: Response
): Promise<SdkRestResult<T>> {
  const json = (await res.json().catch(() => null)) as unknown;
  if (res.ok) {
    return { ok: true, value: json as T };
  }
  const code =
    json && typeof json === "object" && "code" in json
      ? (json as { code?: unknown }).code
      : undefined;
  const retryAfter = Number(res.headers.get("retry-after"));
  return {
    ok: false,
    status: res.status,
    ...(typeof code === "string" ? { code } : {}),
    ...(retryAfter > 0 ? { retryAfterSeconds: retryAfter } : {}),
  };
}

/** A non-2xx response as an `Error`. The message keeps the status and body
 *  text; `status` carries the HTTP status so callers can branch on it (the
 *  processing watch treats a 404 as "upload gone") without parsing it. */
function httpError(
  method: string,
  url: string,
  status: number,
  text: string
): Error & { status: number } {
  return Object.assign(
    new Error(
      `${method} ${url} failed with HTTP ${status}${text ? `: ${text}` : ""}`
    ),
    { status }
  );
}

async function requestJson(
  fetch: typeof globalThis.fetch,
  method: string,
  url: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<unknown> {
  const res = await fetch(url, {
    method,
    headers:
      body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw httpError(method, url, res.status, text);
  }
  // 204 (chats.delete) carries no body — res.json() would throw on it.
  if (res.status === 204) {
    return undefined;
  }
  return res.json();
}

/** Binary leg (voice TTS): same error idiom as {@link requestJson}. */
async function requestBinary(
  fetch: typeof globalThis.fetch,
  method: string,
  url: string,
  body?: unknown
): Promise<ArrayBuffer> {
  const res = await fetch(url, {
    method,
    headers:
      body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw httpError(method, url, res.status, text);
  }
  return res.arrayBuffer();
}

export function createSdkRestSurface(args: {
  baseUrl: string;
  fetch: typeof globalThis.fetch;
}): SdkRestSurface {
  const { baseUrl, fetch } = args;
  const root = `${baseUrl}/api/sdk/v1`;
  const calendarRoot = `${baseUrl}/api/calendar`;
  return {
    calendar: {
      listRooms: (query, options) => {
        const params = new URLSearchParams({
          start: query.start,
          end: query.end,
          timeZone: query.timeZone,
        });
        if (query.attendees?.length) {
          params.set("attendees", query.attendees.join(","));
        }
        return requestJson(
          fetch,
          "GET",
          `${calendarRoot}/rooms?${params.toString()}`,
          undefined,
          options?.signal
        ) as Promise<SdkRoomsResponse>;
      },
      getFavoriteRooms: (options) =>
        requestJson(
          fetch,
          "GET",
          `${calendarRoot}/favorite-rooms`,
          undefined,
          options?.signal
        ) as Promise<SdkFavoriteRoomsResponse>,
      setFavoriteRooms: (input, options) =>
        requestJson(
          fetch,
          "PUT",
          `${calendarRoot}/favorite-rooms`,
          input,
          options?.signal
        ) as Promise<SdkFavoriteRoomsResponse>,
    },
    chats: {
      list: (input) => {
        const params = new URLSearchParams();
        if (input?.limit !== undefined) {
          params.set("limit", String(input.limit));
        }
        if (input?.cursor) {
          params.set("cursor", input.cursor);
        }
        const query = params.toString();
        return requestJson(
          fetch,
          "GET",
          `${root}/chats${query ? `?${query}` : ""}`
        ) as Promise<SdkChatListPage>;
      },
      get: (chatId) =>
        requestJson(
          fetch,
          "GET",
          `${root}/chats/${encodeURIComponent(chatId)}`
        ) as Promise<SdkChatMessages>,
      rename: (chatId, input) =>
        requestJson(
          fetch,
          "PATCH",
          `${root}/chats/${encodeURIComponent(chatId)}`,
          input
        ) as Promise<SdkChatSummary>,
      delete: async (chatId) => {
        await requestJson(
          fetch,
          "DELETE",
          `${root}/chats/${encodeURIComponent(chatId)}`
        );
      },
      saveVoiceSession: (chatId, input, options) =>
        requestResult<SdkVoiceSessionSaved>(
          fetch,
          "POST",
          `${root}/chats/${encodeURIComponent(chatId)}/voice-sessions`,
          input,
          { keepalive: options?.keepalive }
        ),
    },
    feedback: {
      get: (chatId) =>
        requestJson(
          fetch,
          "GET",
          `${root}/chats/${encodeURIComponent(chatId)}/feedback`
        ) as Promise<SdkFeedbackMap>,
      submit: async (chatId, input) => {
        await requestJson(
          fetch,
          "POST",
          `${root}/chats/${encodeURIComponent(chatId)}/feedback`,
          input
        );
      },
    },
    toolApprovals: {
      submit: (input) =>
        requestJson(
          fetch,
          "POST",
          `${root}/tool-approvals`,
          input
        ) as Promise<SdkApprovalResult>,
    },
    models: {
      list: () =>
        requestJson(fetch, "GET", `${root}/models`) as Promise<SdkModelList>,
    },
    skills: {
      listPickerItems: () =>
        requestJson(fetch, "GET", `${root}/skills`) as Promise<
          SdkSkillPickerItem[]
        >,
    },
    artifacts: {
      listPickerItems: () =>
        requestJson(fetch, "GET", `${root}/artifacts`) as Promise<
          SdkArtifactPickerItem[]
        >,
    },
    uploads: {
      create: (input) =>
        requestJson(
          fetch,
          "POST",
          `${root}/uploads`,
          input
        ) as Promise<SdkUploadMinted>,
      finalize: (uploadId, options) =>
        requestJson(
          fetch,
          "POST",
          `${root}/uploads/${encodeURIComponent(uploadId)}/finalize`,
          undefined,
          options?.signal
        ) as Promise<SdkUploadFinalized>,
      get: (uploadId) =>
        requestJson(
          fetch,
          "GET",
          `${root}/uploads/${encodeURIComponent(uploadId)}`
        ) as Promise<SdkUploadStatus>,
      delete: async (uploadId) => {
        await requestJson(
          fetch,
          "DELETE",
          `${root}/uploads/${encodeURIComponent(uploadId)}`
        );
      },
      retryProcessing: async (uploadId) => {
        await requestJson(
          fetch,
          "POST",
          `${root}/uploads/${encodeURIComponent(uploadId)}/retry-processing`
        );
      },
    },
    voice: {
      mintToken: (grant) =>
        requestJson(
          fetch,
          "POST",
          `${root}/voice/ws-token?grant=${grant}`
        ) as Promise<SdkVoiceToken>,
      tts: (input) => requestBinary(fetch, "POST", `${root}/voice/tts`, input),
      liveAvailability: () =>
        requestJson(
          fetch,
          "GET",
          `${root}/voice/live-session`
        ) as Promise<SdkLiveVoiceAvailability>,
      liveSession: (input, options) =>
        requestResult<SdkLiveSessionAnswer>(
          fetch,
          "POST",
          `${root}/voice/live-session`,
          input,
          { signal: options?.signal }
        ),
      endLiveSession: async (callSessionId, options) => {
        const query = new URLSearchParams({
          [LIVE_SESSION_RELEASE_PARAM]: callSessionId,
        });
        await fetch(`${root}/voice/live-session?${query}`, {
          method: "DELETE",
          keepalive: options?.keepalive,
        });
      },
    },
  };
}
